<template>
  <div class="normal-dashboard">
    <h4 class="title">
      {{ $t('myCabinetMenu.sorular') }}
    </h4>

    <!-- Loading state -->
    <div
      v-if="loading"
      class="card dashboard-card"
    >
      <div class="card-body loading-state">
        <div class="spinner-container">
          <div class="spinner" />
        </div>
        <p>{{ $t('login.yklnir') }}</p>
      </div>
    </div>

    <!-- Empty state with icon -->
    <div
      v-else-if="!sectors || sectors.length === 0"
      class="card dashboard-card"
    >
      <div class="card-body empty-state">
        <div class="empty-icon">
          <i class="fa fa-folder-open" />
        </div>
        <p>{{ $t('common.noData', 'No sectors available') }}</p>
      </div>
    </div>

    <!-- Sectors list -->
    <div
      v-else
      class="card dashboard-card"
    >
      <div class="card-body">
        <transition-group
          name="list"
          tag="div"
          class="sectors-container"
        >
          <template v-for="(sector, i) in sectors">
            <div 
              v-if="i > 0" 
              :key="`hr-${i}`" 
              class="sector-divider"
            />

            <router-link
              :key="`sector-${sector.id || i}`"
              class="sector-item"
              :class="{
                'disabled-sector': !sector.enabled && profile !== 'PROCUREMENT',
                'has-unread': sector.unreadCount > 0 && sector.enabled
              }"
              :to="getSectorRoute(sector)"
              :aria-disabled="!sector.enabled && profile !== 'PROCUREMENT'"
            >
              <div class="d-flex justify-content-between align-items-center">
                <div class="sector-info">
                  <sector-icon 
                    :profile="profile" 
                    :sector="sector" 
                    :get-photo="getPhoto"
                  />
                  <span class="name">{{ sector.name || $t('common.unnamed') }}</span>
                </div>
                <div class="sector-counts">
                  <div
                    v-if="sector.unreadCount > 0 && sector.enabled"
                    class="badge bg-success pulse text-light"
                  >
                    {{ sector.unreadCount }}
                  </div>
                  <div
                    v-else-if="sector.requestCount > 0"
                    class="badge badge-light"

                  >
                    {{ sector.requestCount }}
                  </div>
                </div>
              </div>
            </router-link>
          </template>
        </transition-group>
      </div>
    </div>
  </div>
</template>

<script>
import {getPhoto} from "@/repositories/Utils";

/**
 * SectorIcon component - Displays the appropriate icon for a sector based on profile and sector status
 */
const SectorIcon = {
  name: 'SectorIcon',
  props: {
    profile: {
      type: String,
      required: true
    },
    sector: {
      type: Object,
      required: true
    },
    getPhoto: {
      type: Function,
      required: true
    }
  },
  render(h) {
    if (this.profile === 'PROCUREMENT') {
      return h('img', {
        attrs: {
          src: this.getPhoto(this.sector.icon || '', ''),
          alt: this.sector.name || '',
        },
        class: 'icon'
      });
    } else {
      if (this.sector.enabled) {
        return h('img', {
          attrs: {
            src: this.getPhoto(this.sector.icon || '', ''),
            alt: this.sector.name || '',
          },
          class: 'icon'
        });
      } else {
        return h('img', {
          attrs: {
            src: require('@/assets/images/icons/lock.png'),
            alt: this.sector.name || '',
          },
          class: 'icon'
        });
      }
    }
  }
};

/**
 * NormalDashboard component
 * Displays a list of sectors with their respective icons, names, and counts
 */
export default {
  name: 'NormalDashboard',
  components: {
    SectorIcon
  },
  props: {
    /**
     * User profile type
     */
    profile: {
      type: String,
      required: true,
      validator: value => ['PROCUREMENT', 'VENDOR', 'ADMIN'].includes(value)
    },
    /**
     * Array of sector objects
     */
    sectors: {
      type: Array,
      required: true,
      default: () => []
    },
    /**
     * Loading state flag
     */
    loading: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    /**
     * Get photo URL from path and size
     * @param {string} path - Path to the photo
     * @param {string} size - Size of the photo
     * @return {string} - URL of the photo
     */
    getPhoto(path, size) {
      if (!path) return require('@/assets/images/icons/dashboard/vendors.png');
      return getPhoto(path, size);
    },

    /**
     * Get route object for sector
     * @param {Object} sector - Sector object
     * @return {Object} - Route object
     */
    getSectorRoute(sector) {
      if (!sector || !sector.id) {
        return { name: 'dashboard' };
      }
      return { name: 'sector', params: { sectorId: sector.id } };
    }
  }
}
</script>
<style scoped lang="scss">
@import "@/assets/scss/theme/_variables.scss";

.normal-dashboard {
  .title {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 1.5rem;
    color: darken($theme-font-color, 10%);
    letter-spacing: 0.5px;
  }

  // Card styling
  .dashboard-card {
    border: none;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    overflow: hidden;
    margin-bottom: 1.5rem;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
    }
  }

  .card-body {
    padding: 1.25rem;

    // Loading state
    &.loading-state {
      text-align: center;
      padding: 3rem 1.5rem;
      color: $theme-font-color;

      .spinner-container {
        margin-bottom: 1rem;

        .spinner {
          width: 40px;
          height: 40px;
          margin: 0 auto;
          border: 3px solid rgba(#2062FB, 0.2);
          border-top-color: #2062FB;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
      }

      p {
        margin: 0;
        font-size: 16px;
        color: lighten($theme-font-color, 15%);
      }
    }

    // Empty state
    &.empty-state {
      text-align: center;
      padding: 3rem 1.5rem;
      color: $theme-font-color;

      .empty-icon {
        margin-bottom: 1rem;
        font-size: 2.5rem;
        color: lighten($theme-font-color, 40%);
      }

      p {
        margin: 0;
        font-size: 16px;
        color: lighten($theme-font-color, 15%);
      }
    }
  }

  // Sectors container
  .sectors-container {
    position: relative;
  }

  // Divider between sectors
  .sector-divider {
    height: 1px;
    background: linear-gradient(to right, rgba(#2062FB, 0.05), rgba(#2062FB, 0.15), rgba(#2062FB, 0.05));
    margin: 8px 0;
  }

  // Sector item styling
  .sector-item {
    display: block;
    cursor: pointer;
    padding: 12px 14px;
    font-size: 15px;
    color: $theme-font-color;
    border-radius: 8px;
    transition: all 0.25s ease;
    text-decoration: none;
    position: relative;
    overflow: hidden;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 4px;
      height: 100%;
      background-color: transparent;
      transition: background-color 0.25s ease;
    }

    &.has-unread {
      &::after {
        background-color: #28a745;
      }
    }

    &.disabled-sector {
      cursor: not-allowed;
      opacity: 0.8;

      .icon {
        background-color: rgba(#E89919, 0.15);
        border: 1px solid rgba(#E89919, 0.3);
      }

      &:hover {
        background-color: rgba(#E89919, 0.08);
      }

      &::after {
        background-color: #E89919;
      }
    }

    .sector-info {
      display: flex;
      align-items: center;
      max-width: 80%;

      .name {
        font-weight: 500;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        transition: color 0.2s ease;
      }
    }

    .sector-counts {
      .badge {
        padding: 0.4em 0.7em;
        font-size: 0.85em;
        font-weight: 500;
        border-radius: 50px;
        min-width: 26px;
        text-align: center;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        transition: transform 0.2s ease;

        &.bg-success {
          background-color: #28a745 !important;
        }

        &.badge-light {
          background-color: #f8f9fa;
          color: #212529;
        }

        &.pulse {
          animation: pulse 2s infinite;
        }
      }
    }

    .icon {
      width: 38px;
      height: 38px;
      padding: 6px;
      background-color: rgba(#2062FB, 0.08);
      border-radius: 10px;
      margin-right: 12px;
      object-fit: contain;
      transition: all 0.25s ease;
      border: 1px solid transparent;
    }

    &:hover {
      background-color: rgba(#2062FB, 0.08);
      transform: translateX(4px);

      .icon {
        background-color: rgba(#2062FB, 0.15);
        transform: scale(1.05);
      }

      .name {
        color: #2062FB;
      }

      .sector-counts .badge {
        transform: scale(1.1);
      }
    }
  }

  // List transitions
  .list-enter-active, .list-leave-active {
    transition: all 0.5s;
  }
  .list-enter, .list-leave-to {
    opacity: 0;
    transform: translateY(30px);
  }

  // Animations
  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  @keyframes pulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.7);
    }
    70% {
      transform: scale(1.05);
      box-shadow: 0 0 0 6px rgba(40, 167, 69, 0);
    }
    100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(40, 167, 69, 0);
    }
  }

  // Responsive design
  @media (max-width: 992px) {
    .dashboard-card {
      margin-bottom: 1rem;
    }
  }

  @media (max-width: 768px) {
    .title {
      font-size: 18px;
      margin-bottom: 1rem;
    }

    .sector-item {
      padding: 10px 12px;

      .sector-info {
        max-width: 70%;

        .icon {
          width: 34px;
          height: 34px;
          margin-right: 10px;
        }
      }
    }
  }

  @media (max-width: 576px) {
    .card-body {
      padding: 1rem;
    }

    .sector-item {
      padding: 8px 10px;

      .sector-info {
        max-width: 65%;

        .icon {
          width: 30px;
          height: 30px;
          margin-right: 8px;
        }

        .name {
          font-size: 14px;
        }
      }

      .sector-counts .badge {
        padding: 0.35em 0.6em;
        font-size: 0.8em;
      }
    }
  }
}
</style>
